import { parseSearchQuery, webStorage } from 'sidecar-utilities'

/** Whether this tab is an admin impersonating a member. */
export const isImpersonating = () => !!sessionStorage.getItem('isAdmin')

/**
 * Ends this tab's impersonation. Without this the tab re-asserts `isAdmin` on
 * every render, and every other tab wipes the member's token in response.
 */
export const endImpersonation = () => {
  sessionStorage.removeItem('isAdmin')
  webStorage.removeItem('isAdmin')
  webStorage.removeItem('adminUuid')
}

/**
 * Processes impersonation query params and sets webStorage/sessionStorage
 * values so the app treats the user as an impersonating admin. This is safe to
 * call during render because it only writes to storage (no React state
 * updates).
 *
 * @param {object} location The location object from react-router-dom
 */
export const setImpersonationStorage = location => {
  // look at the query params to see if this is an admin trying
  // to come in as a member (member token, member everything else),
  // if so, set what needs setting so they can impersonate.
  const { t, aud, sub, a } = parseSearchQuery(location.search)

  // if we have 'a', we're supposed to be an admin. Tell both session
  // and local, we're admins. Session will help keep local from remembering
  // too much. If session forgets, so should local. We're using local for
  // a consistent webStorage API, but we're using session because it can
  // forget things that need to be forgotten when the user leaves.
  // Set before the token: other tabs act on the token's storage event and
  // check `isAdmin` to tell that this tab is impersonating, not signing in.
  if (a) {
    sessionStorage.setItem('isAdmin', true)
    webStorage.setItem('isAdmin', true)
    webStorage.setItem('adminUuid', a)
  }

  // The token will be an admin token (must be valid)
  t && webStorage.setItem('token', t)

  // The acc will be the accountUuid of the member
  aud && webStorage.setItem('aud', aud)

  // The sub will be the memberUuid of the member.
  // We need to set searchingAs because it stays set to the last member
  // that was searched for, which may be from a different account.
  // Left alone, it wouldn't update this until you get to care detail
  // which is a little too late to be useful. Also, bad UX.
  sub && webStorage.setItem('sub', sub)
  sub && webStorage.setItem('searchingAsUuid', sub)

  // An impersonation runs on the admin token stored by the link above. Booting
  // without one means that token is gone, so the impersonation is over.
  if (!t && isImpersonating() && !webStorage.getItem('token'))
    endImpersonation()

  // if session is saying we're an admin, we must still be one;
  // if we're an admin, set local so everything else knows too.
  if (sessionStorage.getItem('isAdmin')) {
    webStorage.setItem('isAdmin', true)
  }

  // if local says we're admins, but session says we aren't,
  // clear everything and make the user start over, local knows too much!
  if (webStorage.getItem('isAdmin') && !sessionStorage.getItem('isAdmin')) {
    webStorage.removeItem('token')
    webStorage.removeItem('aud')
    webStorage.removeItem('sub')
    webStorage.removeItem('searchingAsUuid')
    webStorage.removeItem('isAdmin')
    webStorage.removeItem('adminUuid')
  }
}

/**
 * Cleans impersonation query params from the URL. This calls navigate() which
 * updates React Router state, so it must only be called inside a useEffect —
 * never during render.
 *
 * @param {object} location The location object from react-router-dom
 * @param {function} navigate The navigate function from react-router-dom
 */
export const cleanImpersonationParams = (location, navigate) => {
  const { t, aud, sub, a } = parseSearchQuery(location.search)

  // Only clean up the URL if we have impersonation parameters to remove
  if (t || aud || sub || a) {
    const searchParams = new URLSearchParams(location.search)
    // Remove all impersonation-related query parameters from the URL
    searchParams.delete('t')
    searchParams.delete('aud')
    searchParams.delete('sub')
    searchParams.delete('a')

    // Update the URL without causing a page reload
    // This keeps the URL clean and removes sensitive information
    navigate({
      pathname: location.pathname,
      search: searchParams.toString() || undefined,
    })
  }
}
